/**
 * 地理定位 Mixin
 * 提供自動定位、快取管理、權限處理等功能
 *
 * @version 1.0.0
 */
export default {
  data() {
    return {
      isLocating: false,           // 正在定位中
      locationPermission: null,     // 權限狀態: 'granted' | 'denied' | 'prompt'
      userCoords: null,            // 使用者座標 { lat, lng }
      showCityDistrictSelector: false,  // 是否顯示縣市選單
      showGeolocationGuide: false  // 是否顯示定位權限指引
    };
  },

  methods: {
    /**
     * 檢查是否為首次訪問（沒有定位快取）
     * @returns {Boolean}
     */
    isFirstVisit() {
      return !localStorage.getItem('geolocationCache');
    },

    /**
     * 取得定位快取
     * @returns {Object|null} { coords, permission, timestamp }
     */
    getGeolocationCache() {
      const cache = localStorage.getItem('geolocationCache');
      if (!cache) return null;

      try {
        const parsed = JSON.parse(cache);

        // 檢查是否過期（24小時）
        const expiresAt = parsed.timestamp + 24 * 60 * 60 * 1000;
        if (Date.now() > expiresAt) {
          console.log('Geolocation cache expired');
          this.clearGeolocationCache();
          return null;
        }

        return parsed;
      } catch (e) {
        console.error('Failed to parse geolocation cache:', e);
        this.clearGeolocationCache();
        return null;
      }
    },

    /**
     * 儲存定位快取
     * @param {Object} data { coords, permission }
     */
    saveGeolocationCache(data) {
      const cache = {
        ...data,
        timestamp: Date.now()
      };

      try {
        localStorage.setItem('geolocationCache', JSON.stringify(cache));
        console.log('Geolocation cache saved:', cache);
      } catch (e) {
        console.error('Failed to save geolocation cache:', e);
      }
    },

    /**
     * 清除定位快取
     */
    clearGeolocationCache() {
      localStorage.removeItem('geolocationCache');
      this.userCoords = null;
      this.locationPermission = null;
      console.log('Geolocation cache cleared');
    },

    /**
     * 執行自動定位（首次訪問或重新定位）
     * @param {Boolean} force - 強制重新定位（忽略快取）
     * @param {Boolean} isManualRetry - 是否為用戶手動重試（從 GeolocationGuide 觸發）
     */
    async autoGeolocation(force = false, isManualRetry = false) {
      console.log('Auto geolocation called with force:', force, 'isManualRetry:', isManualRetry);
      // 1. 檢查快取（除非強制重新定位）
      if (!force) {
        const cache = this.getGeolocationCache();

        if (cache && cache.permission === 'granted') {
          console.log('Using cached geolocation:', cache);
          this.locationPermission = 'granted';
          this.userCoords = cache.coords;
          this.applyGeolocationFilter(cache.coords);
          return;
        }

        // 如果之前被拒絕，直接顯示縣市選單
        if (cache && cache.permission === 'denied') {
          console.log('User previously denied geolocation');
          this.locationPermission = 'denied';
          this.showCityDistrictSelector = true;
          return;
        }
      }

      // 2. 檢查瀏覽器支援
      if (!navigator.geolocation) {
        console.warn('Geolocation API not supported');
        this.showCityDistrictSelector = true;
        return;
      }

      // 3. 請求定位
      this.isLocating = true;

      try {
        const position = await this.getCurrentPosition();
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        console.log('Geolocation obtained:', coords);
        
        // 4. 使用統一方法更新狀態與觸發篩選
        this.updateGeolocation(coords);

        // 5. 儲存快取（只需儲存座標，不需要行政區資訊）
        const cacheData = {
          coords,
          permission: 'granted'
        };
        this.saveGeolocationCache(cacheData);

      } catch (error) {
        console.error('Geolocation error:', error);

        // 區分錯誤類型：只有真正被拒絕才設為 'denied'
        if (error.code === error.PERMISSION_DENIED) {
          this.locationPermission = 'denied';
          // 只有權限被拒絕才儲存快取，避免下次自動跳過定位
          this.saveGeolocationCache({ permission: 'denied' });
        } else {
          // POSITION_UNAVAILABLE 或 TIMEOUT 是技術性失敗，不是權限問題
          this.locationPermission = 'unavailable';
          // 不儲存快取，下次還可以重試
        }

        // 顯示縣市選單作為備用方案
        this.showCityDistrictSelector = true;

        // 友善的錯誤提示（傳入 isManualRetry 參數）
        this.handleGeolocationError(error, isManualRetry);

      } finally {
        this.isLocating = false;
      }
    },

    /**
     * Promise 版本的 getCurrentPosition
     * @param {Object} options - Geolocation API 選項
     * @returns {Promise<Position>}
     */
    getCurrentPosition(options = {}) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            timeout: 10000,           // 10秒超時
            maximumAge: 600000,       // 允許使用10分鐘內的快取位置
            enableHighAccuracy: false, // 不需要高精度，加快速度
            ...options
          }
        );
      });
    },

    /**
     * 處理定位錯誤
     * @param {PositionError} error
     * @param {Boolean} isManualRetry - 是否為用戶手動重試
     */
    handleGeolocationError(error, isManualRetry = false) {
      let message = '無法取得您的位置';

      switch (error.code) {
        case error.PERMISSION_DENIED:
          message = '您拒絕了定位請求，請使用縣市選單選擇地區';
          break;
        case error.POSITION_UNAVAILABLE:
          message = '位置資訊無法取得，請使用縣市選單選擇地區';
          break;
        case error.TIMEOUT:
          message = '定位請求超時，請使用縣市選單選擇地區';
          break;
      }

      // 顯示錯誤訊息
      console.warn(message);

      // 使用彈窗顯示錯誤（確保用戶看到）
      if (error.code === error.PERMISSION_DENIED && !isManualRetry) {
        // 自動定位被拒：只顯示提示列（alert-inline），不自動彈窗
        // locationPermission 已設為 'denied'，geolocation-alert-inline 會自動顯示
      } else {
        // 其他情況都顯示錯誤訊息：
        // 1. 手動重試時權限被拒絕（isManualRetry = true）
        // 2. 位置不可用、超時等其他錯誤
        if (this.$swal) {
          this.$swal({
            title: '定位失敗',
            text: message,
            //icon: 'warning',
            confirmButtonText: '確定',
            confirmButtonColor: '#F90'
          });
        } else {
          // 備援：如果沒有 $swal，使用原生 alert
          alert(`定位失敗\n\n${message}`);
        }
      }
    },

    /**
     * 統一更新定位資訊與權限狀態
     * @param {Object} coords { lat, lng }
     */
    updateGeolocation(coords) {
      if (coords && coords.lat && coords.lng) {
        console.log('Updating geolocation state globally:', coords);
        this.userCoords = coords;
        this.locationPermission = 'granted';
        
        // 觸發各元件自定義的篩選邏輯
        this.applyGeolocationFilter(coords);
      }
    },

    /**
     * 套用定位篩選（需由各元件覆寫實作）
     * 直接使用使用者座標計算距離並篩選附近店家
     * @param {Object} coords { lat, lng }
     */
    applyGeolocationFilter(coords) {
      console.warn('applyGeolocationFilter should be implemented by component');
    },

    /**
     * 計算兩點之間的距離（Haversine 公式）
     * @param {Number} lat1 起點緯度
     * @param {Number} lng1 起點經度
     * @param {Number} lat2 終點緯度
     * @param {Number} lng2 終點經度
     * @returns {Number} 距離（公里）
     */
    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371; // 地球半徑（公里）
      const dLat = this.toRadians(lat2 - lat1);
      const dLng = this.toRadians(lng2 - lng1);

      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(this.toRadians(lat1)) *
                Math.cos(this.toRadians(lat2)) *
                Math.sin(dLng/2) * Math.sin(dLng/2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    },

    /**
     * 角度轉弧度
     */
    toRadians(degrees) {
      return degrees * Math.PI / 180;
    },

    /**
     * 檢測是否為 iOS 裝置
     * @returns {Boolean}
     */
    isIOSDevice() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent);
    },

    /**
     * 檢查定位權限狀態（部分瀏覽器支援）
     * @returns {Promise<String>} 'granted' | 'denied' | 'prompt'
     */
    async checkGeolocationPermission() {
      if (!navigator.permissions) {
        return 'prompt'; // 不支援 Permissions API
      }

      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        return result.state; // 'granted' | 'denied' | 'prompt'
      } catch (e) {
        console.warn('Permissions API not supported:', e);
        return 'prompt';
      }
    },

    /**
     * 偵測使用者的瀏覽器類型
     * @returns {String} 'chrome' | 'safari' | 'firefox' | 'edge' | 'unknown'
     */
    detectBrowser() {
      const ua = navigator.userAgent;

      if (ua.includes('Edg/')) return 'edge';
      if (ua.includes('Chrome') && !ua.includes('Edg/')) return 'chrome';
      if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari';
      if (ua.includes('Firefox')) return 'firefox';

      return 'unknown';
    },

    /**
     * 取得針對不同瀏覽器的定位權限設定指引
     * @returns {Object} { browser, icon, title, steps }
     */
    getGeolocationGuide() {
      const browser = this.detectBrowser();
      const isIOS = this.isIOSDevice();

      const guides = {
        chrome: {
          browser: 'Chrome',
          icon: '🌐',
          title: '如何在 Chrome 開啟定位權限',
          steps: [
            '點擊網址列左側的「鎖頭」圖示',
            '找到「位置」設定',
            '選擇「允許」',
            '重新載入頁面'
          ]
        },
        safari: isIOS ? {
          browser: 'Safari (iOS)',
          icon: '📱',
          title: '如何在 iPhone/iPad 開啟定位權限',
          steps: [
            '開啟「設定」App',
            '向下滑動找到「Safari」',
            '點選「位置」',
            '選擇「詢問」或「允許」',
            '返回網頁並重新整理'
          ]
        } : {
          browser: 'Safari (Mac)',
          icon: '🖥️',
          title: '如何在 Safari 開啟定位權限',
          steps: [
            '點擊「Safari」選單 → 「偏好設定」',
            '選擇「網站」標籤頁',
            '左側選單點選「位置」',
            '找到本網站並選擇「允許」',
            '重新載入頁面'
          ]
        },
        firefox: {
          browser: 'Firefox',
          icon: '🦊',
          title: '如何在 Firefox 開啟定位權限',
          steps: [
            '點擊網址列左側的「鎖頭」或「i」圖示',
            '找到「權限」區塊',
            '點選「存取你的位置」旁的「X」清除封鎖',
            '重新載入頁面，並在提示時選擇「允許」'
          ]
        },
        edge: {
          browser: 'Edge',
          icon: '🌊',
          title: '如何在 Edge 開啟定位權限',
          steps: [
            '點擊網址列左側的「鎖頭」圖示',
            '找到「位置」權限',
            '選擇「允許」',
            '重新載入頁面'
          ]
        },
        unknown: {
          browser: '您的瀏覽器',
          icon: '🌐',
          title: '如何開啟定位權限',
          steps: [
            '點擊網址列旁的網站設定圖示（通常是鎖頭或 i 圖示）',
            '找到「位置」或「定位」權限設定',
            '選擇「允許」',
            '重新載入頁面'
          ]
        }
      };

      return guides[browser] || guides.unknown;
    }
  }
};
